import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GridColDef, GridRenderCellParams, GridRowParams, GridActionsCellItem } from '@mui/x-data-grid';
import { Button, Box, Typography, Alert, Snackbar, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useNavigate } from 'react-router-dom';

import { DataTable } from '../components/DataTable';
import { ProductForm } from '../components/products/ProductForm';
import { productsApi, locationAssignmentsApi } from '../api/services';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { extractApiError } from '../utils/apiErrors';
import type { Product } from '../api/config';

export const ProductsPage: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState<Product | undefined>();
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'error' | 'success' } | null>(null);
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const emptyFilters = React.useMemo(() => ({} as Record<string, never>), []);

  const {
    rows: products,
    rowCount,
    isLoading,
    error,
    paginationModel,
    onPaginationModelChange,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
  } = usePaginatedQuery({
    queryKey: ['products'],
    queryFn: (f, p) => productsApi.getPage(f, p),
    filters: emptyFilters,
    defaultSort: { field: 'name', sort: 'asc' },
  });

  // Fetch assignment counts for all products (count of unique locations with active assignments)
  const { data: assignmentCounts = {} } = useQuery({
    queryKey: ['product_assignment_counts'],
    queryFn: () => locationAssignmentsApi.getCounts(),
  });

  const createMutation = useMutation({
    mutationFn: productsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setSnackbar({ message: 'Product created successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to create product'), severity: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Product> }) =>
      productsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setSnackbar({ message: 'Product updated successfully', severity: 'success' });
      handleCloseForm();
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to update product'), severity: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: productsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setSnackbar({ message: 'Product deleted successfully', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ message: extractApiError(error, 'Failed to delete product'), severity: 'error' });
    },
  });

  const columns: GridColDef[] = [
    { field: 'sku', headerName: 'SKU', width: 150, resizable: true },
    { field: 'name', headerName: 'Name', width: 200, resizable: true },
    {
      field: 'category',
      headerName: 'Category',
      width: 150,
      resizable: true,
      valueGetter: (params: GridRenderCellParams<Product>) => {
        return params.row?.product_equivalency?.product_equivalency_category?.name || '';
      },
    },
    {
      field: 'manufacturer',
      headerName: 'Manufacturer',
      width: 180,
      resizable: true,
      valueGetter: (params: GridRenderCellParams<Product>) => {
        return params.row?.manufacturer?.name || '';
      },
    },
    {
      field: 'reorder_threshold',
      headerName: 'Reorder Threshold',
      width: 150,
      type: 'number',
      resizable: true,
      valueGetter: (params: GridRenderCellParams<Product>) => {
        return params.row?.reorder_threshold ?? 0;
      },
    },
    {
      field: 'units_per_box',
      headerName: 'Units per Box',
      width: 120,
      type: 'number',
      resizable: true,
      valueGetter: (params: GridRenderCellParams<Product>) => {
        return params.row?.units_per_box ?? null;
      },
    },
    {
      field: 'assignment_count',
      headerName: 'Active Assignments',
      filterable: false,
      width: 150,
      resizable: true,
      renderCell: (params: GridRenderCellParams<Product>) => {
        const count = assignmentCounts[params.row?.sku || ''] || 0;
        return (
          <Chip
            label={`${count} assignments`}
            color={count > 0 ? 'primary' : 'default'}
            size="small"
            variant="outlined"
          />
        );
      },
    },
  ];

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedProduct(undefined);
    createMutation.reset();
    updateMutation.reset();
  };

  const mutationError = createMutation.error || updateMutation.error;

  const handleSubmit = (data: Partial<Product> & { product_equivalency_category_id?: number; product_equivalency_id?: number; hide_when_zero?: boolean }) => {
    createMutation.reset();
    updateMutation.reset();
    if (selectedProduct) {
      updateMutation.mutate({ id: selectedProduct.sku, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (id: string) => {
    const product = products.find((prod) => prod.sku === id);
    if (product) {
      setSelectedProduct(product);
      setIsFormOpen(true);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      deleteMutation.mutate(id);
    }
  };

  const customActions = (params: GridRowParams) => [
    <GridActionsCellItem
      key="assignments"
      icon={<AssignmentIcon />}
      label="Manage Assignments"
      onClick={() => navigate(`/location_assignments?sku=${params.id}`)}
      showInMenu={false}
    />,
  ];

  if (error) {
    return <Alert severity="error">Error loading products</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" component="h1">
          Products
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsFormOpen(true)}
        >
          Add Product
        </Button>
      </Box>

      <DataTable
        rows={products}
        columns={columns}
        loading={isLoading}
        onEditClick={handleEdit}
        onDeleteClick={handleDelete}
        getRowId={(row: Product) => row.sku}
        customActions={customActions}
        fullHeight
        paginationMode="server"
        sortingMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        pageSizeOptions={[25, 50, 100]}
      />

      <ProductForm
        open={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        initialData={selectedProduct}
        error={mutationError ? extractApiError(mutationError) : undefined}
        isPending={createMutation.isPending || updateMutation.isPending}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
      >
        <Box>
          {snackbar && (
            <Alert 
              severity={snackbar.severity} 
              onClose={() => setSnackbar(null)}
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          )}
        </Box>
      </Snackbar>
    </Box>
  );
}; 